import { create } from 'zustand';

/**
 * F5.3 — Personal pins UI state.
 *
 * Pin list lives in TanStack Query under ['pins'] (mine) and ['pins-demo']
 * (anonymous demo). This store holds only the lightweight UI state.
 *
 * Console hack: window.__pinsDebug exposes mode toggles. Set
 * `__pinsDebug.showBoth()` after signing in to render both your own pins
 * AND the demo set (useful for screenshots or to confirm the demo file
 * still loads in prod).
 */

export type PinsMode = 'auto' | 'mine-only' | 'demo-only' | 'both' | 'hidden';

interface PinsState {
  /** What to render. 'auto' = mine if signed in, demo if anonymous (default). */
  mode: PinsMode;
  setMode: (m: PinsMode) => void;
  // Pre-filled coordinates when "Add pin" is opened from a comune.
  addDialog:
    | null
    | {
        lat: number;
        lon: number;
        comuneIstat?: string;
        comuneName?: string;
      };
  openAddDialog: (init: NonNullable<PinsState['addDialog']>) => void;
  closeAddDialog: () => void;
  // US-09: bulk-import dialog for paste/CSV → geocode → pins.
  bulkImportOpen: boolean;
  openBulkImport: () => void;
  closeBulkImport: () => void;
}

export const usePinsStore = create<PinsState>((set) => ({
  mode: 'auto',
  setMode: (mode) => set({ mode }),
  addDialog: null,
  openAddDialog: (init) => set({ addDialog: init }),
  closeAddDialog: () => set({ addDialog: null }),
  bulkImportOpen: false,
  openBulkImport: () => set({ bulkImportOpen: true }),
  closeBulkImport: () => set({ bulkImportOpen: false }),
}));

// ─── Console hack ────────────────────────────────────────────────────────────
// Exposed once on first import (main bundle is single-instance).
// Usage in DevTools:
//   __pinsDebug.showMine()    → render only personal pins
//   __pinsDebug.showDemo()    → render only demo pins (even when signed in)
//   __pinsDebug.showBoth()    → render both layers
//   __pinsDebug.hide()        → render nothing
//   __pinsDebug.auto()        → default heuristic (mine if signed in, demo if anon)
if (typeof window !== 'undefined') {
  (window as unknown as { __pinsDebug?: unknown }).__pinsDebug = {
    showMine: () => usePinsStore.getState().setMode('mine-only'),
    showDemo: () => usePinsStore.getState().setMode('demo-only'),
    showBoth: () => usePinsStore.getState().setMode('both'),
    hide: () => usePinsStore.getState().setMode('hidden'),
    auto: () => usePinsStore.getState().setMode('auto'),
    current: () => usePinsStore.getState().mode,
  };
}
